/* ============================================================================
   ONGROK slide cart — all drawer behaviour, in load order.

   WHY THIS IS AN ASSET AND NOT INLINE
   It was two inline <script> blocks in snippets/cart-shipping-anim.liquid and
   snippets/cart-drawer-js.liquid. Because the drawer is rendered from layout/theme.liquid,
   that shipped ~15KB of uncacheable JS on EVERY PAGE of the site, re-sent per page view.
   Measured 2026-09-09. It is now one deferred, cached request.

   Nothing in here needs Liquid — there was none in either block. If a future change needs a
   shop or cart value, put it in a JSON island in the snippet and read it here; do NOT move
   this file back into a snippet to get Liquid.

   IT IS LOADED WITH defer, FROM snippets/cart-drawer-js.liquid.
   defer is correct and deliberate: nothing here must run mid-parse. Both IIFEs attach
   delegated listeners and a MutationObserver to nodes that the server renders, and the
   observer only ever fires later, when theme.js re-renders the cart body. A JS load-order
   change once broke the live homepage carousel, so this was verified on the draft before
   being written down.

   ORDER WITHIN THIS FILE IS LOAD-BEARING: the animation block defines window.ogShipPrime and
   window.ogShipResetFill, which the drawer block calls. It must stay first.
   ============================================================================ */

/* ============================================================================
   1. SHIPPING-BAR FILL ANIMATION
   (was snippets/cart-shipping-anim.liquid)

   WHY THIS EXISTS
   theme.js destroys and rebuilds the drawer body on every cart change, so the fill is a BRAND
   NEW node each time. A CSS transition cannot animate a node that did not exist a frame
   earlier — it paints straight at its final value. That is why the bar jumped.

   So the previous percentage is remembered here, the new node is put back at that value, and
   then released to its real target — a change the browser will animate.

   IT MUST RUN BEFORE THE BROWSER PAINTS. DO NOT PUT IT BEHIND setTimeout.
   The first version ran on a 40ms debounce, i.e. AFTER the new fill had already painted at its
   final value. The customer saw the bar appear at the new position, snap BACKWARDS to the old
   one, then animate forwards — reported as "moves back and forth".

   prime() is therefore called synchronously from the MutationObserver callback in section 2,
   which runs as a microtask before the next paint, so the rewind is never visible. Keep it
   that way: if this is ever wrapped in a timer or an rAF, the backward jump comes straight
   back.

   release() then hands it to the target. rAF is preferred, with a timer fallback because rAF
   does not run in a hidden or backgrounded tab — caught in testing, where the fill stayed at
   the old value because the rAF callback never fired.

   IT ANIMATES transform, NOT width. Animating width forces a layout on every frame and was
   visibly jerky. The fill is full width and squashed with scaleX from the left, which the
   compositor handles.
   ============================================================================ */
(function () {
  'use strict';
  var DRAWER = '#slideout-ajax-cart';
  var lastPct = null;
  var pending = null;

  function fillEl() { return document.querySelector(DRAWER + ' .og-ship__fill'); }
  function toScale(pct) { return 'scaleX(' + (pct / 100) + ')'; }

  window.ogShipResetFill = function () { lastPct = 0; };

  /* Synchronous: rewind the freshly rendered fill to the previous value before it paints. */
  window.ogShipPrime = function () {
    var fill = fillEl();
    if (!fill) return;

    var target = parseFloat(fill.getAttribute('data-og-ship-pct'));
    if (isNaN(target)) return;

    if (lastPct === null || lastPct === target) {
      lastPct = target;
      return;
    }

    var from = lastPct;
    lastPct = target;

    fill.style.transition = 'none';
    fill.style.transform = toScale(from);
    void fill.offsetWidth;        /* commit the start value in this frame */
    fill.style.transition = '';   /* hand the transition back to the stylesheet */

    if (pending) { clearTimeout(pending); pending = null; }

    var release = function () {
      var f = fillEl();
      if (f) f.style.transform = toScale(target);
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(release);
    /* Fallback: rAF is paused in hidden tabs. Same value, so double-applying is harmless. */
    pending = setTimeout(release, 60);
  };
})();

/* ============================================================================
   2. ACTIONS ROW (discount code + order note), SCROLL HINT, EMPTY STATE
   (was snippets/cart-drawer-js.liquid)

   DELEGATION: the cart body is replaced wholesale on every quantity change, so listeners are
   bound to document and matched with closest(), never bound to the nodes themselves.
   ============================================================================ */
(function () {
  'use strict';

  var DRAWER = '#slideout-ajax-cart';

  function drawerEl()  { return document.querySelector(DRAWER); }
  function actionsEl() { return document.querySelector('[data-og-actions]'); }
  function linesEl()   { return document.querySelector(DRAWER + ' .og-lines'); }

  function setAttr(el, key, val) { if (el) el.setAttribute('data-' + key, val); }

  function readCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  /* ---------------------------------------------------------------------------
     EMPTY CART

     When the customer removes the LAST item, theme.js does not re-render the drawer body —
     it only strips `hide` from .js-cart-empty and hides the form. Everything the server
     rendered above the form keeps its old values, so the drawer showed "1 item" and
     "$62.40 away from free US shipping" directly above "Your cart is currently empty."
     Reproduced 2026-09-03. Removing one of SEVERAL items is fine; that path re-renders.

     So the one signal theme.js does maintain — the visibility of .js-cart-empty — is
     mirrored onto the drawer as .is-cart-empty, and the stale pieces are hidden in CSS.
  --------------------------------------------------------------------------- */
  function syncEmptyState() {
    var host = drawerEl();
    if (!host) return;
    var empty = host.querySelector('.js-cart-empty');
    var isEmpty = !!empty && !empty.classList.contains('hide');
    host.classList.toggle('is-cart-empty', isEmpty);
    /* Re-adding after an empty cart should fill from zero, not snap down from a stale value. */
    if (isEmpty && window.ogShipResetFill) window.ogShipResetFill();
  }

  /* ---------------------------------------------------------------------------
     SCROLL HINT — is-scrollable only.

     DELIBERATELY SIMPLE, SO IT DOES NOT GET "IMPROVED" BACK. The first design also removed
     the fade at the end of the list (an is-bottom class). Three mechanisms were tried and
     none could be verified here:
       1. capture-phase scroll listener on document — never fired; scroll does not bubble;
       2. listener bound straight to the list — scrollTop moving 0 -> 263 dispatched NO scroll
          event at all, even to a listener attached seconds earlier;
       3. a 1px sentinel plus IntersectionObserver rooted on the list — zero callbacks.
     The fade stays on while the list is scrollable, and the list carries bottom padding equal
     to the fade so nothing is obscured at the end. See section C of assets/og-core.css.
  --------------------------------------------------------------------------- */
  function syncScrollHint() {
    var el = linesEl();
    if (!el) return;
    el.classList.toggle('is-scrollable', (el.scrollHeight - el.clientHeight) > 4);
  }

  function syncSlow() { syncEmptyState(); syncScrollHint(); }

  window.addEventListener('resize', syncScrollHint);

  (function watchForRerenders() {
    var host = document.querySelector(DRAWER + ' .js-ajax-cart-content');
    if (!host || !window.MutationObserver) return;
    var pending = null;

    /* attributes:true matters — the empty-state transition is a CLASS change on
       .js-cart-empty, not a subtree replacement, so childList alone would miss it. */
    new MutationObserver(function () {
      /* SYNCHRONOUS, and it must stay that way. This callback runs as a microtask, before the
         browser paints the newly injected cart body. Priming the fill here means the rewind
         to the previous value is never visible. Behind a timer it painted at the new value
         first and the bar visibly jumped backwards before animating forward. */
      if (window.ogShipPrime) window.ogShipPrime();

      /* Everything else is cheap to defer and benefits from coalescing. */
      if (pending) clearTimeout(pending);
      pending = setTimeout(syncSlow, 40);
    }).observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  })();

  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.AddtoCart, .js-cart-remove, [data-slideout-direction], [popovertarget="slideout-ajax-cart"]')) {
      setTimeout(syncSlow, 400);
      setTimeout(syncSlow, 1200);
      setTimeout(syncSlow, 2500);
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(syncSlow, 400); });
  } else {
    setTimeout(syncSlow, 400);
  }

  /* ---------------------------------------------------------------------------
     ONE PANEL AT A TIME
     data-panel is "none" | "promo" | "note". Clicking an open toggle closes it.
  --------------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var toggle = t.closest('[data-og-toggle]');
    if (!toggle) return;
    e.preventDefault();

    var el = actionsEl();
    if (!el) return;
    var want = toggle.getAttribute('data-og-toggle');
    var next = el.getAttribute('data-panel') === want ? 'none' : want;
    setAttr(el, 'panel', next);

    if (next === 'promo') {
      var input = el.querySelector('[data-og-promo-input]');
      if (input) input.focus();
    } else if (next === 'note') {
      var note = el.querySelector('[data-og-note]');
      if (note) note.focus();
    }
    setTimeout(syncScrollHint, 60);
  });

  /* ---------------------------------------------------------------------------
     ORDER NOTE

     Persisted with POST /cart/update.js, debounced while typing.

     IMPORTANT: this deliberately does NOT re-render the drawer afterwards. A re-render
     replaces the textarea, which would throw away focus and the caret mid-sentence. The note
     is written to the cart and the label updated in place; the next natural re-render picks
     the value back up from cart.note, which the server prints into the textarea.

     The field has no name attribute — Vantage's own note textarea is name="note" in the same
     form, and two would collide on submit. See snippets/cart-promo.liquid.
  --------------------------------------------------------------------------- */
  var noteTimer = null;

  function saveNote(value) {
    var el = actionsEl();
    var status = el && el.querySelector('[data-og-note-status]');
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: value })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var cur = actionsEl();
        if (cur) setAttr(cur, 'note', (value && value.trim()) ? 'true' : 'false');
        if (status) status.textContent = (value && value.trim()) ? 'Saved' : '';
        return cart;
      })
      .catch(function () {
        if (status) status.textContent = "Couldn't save your note — it will be lost at checkout.";
      });
  }

  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('[data-og-note]')) return;
    var el = actionsEl();
    var status = el && el.querySelector('[data-og-note-status]');
    if (status) status.textContent = 'Saving…';
    if (noteTimer) clearTimeout(noteTimer);
    var value = t.value;
    noteTimer = setTimeout(function () { saveNote(value); }, 600);
  });

  /* Leaving the field commits immediately rather than waiting out the debounce. */
  document.addEventListener('blur', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('[data-og-note]')) return;
    if (noteTimer) { clearTimeout(noteTimer); noteTimer = null; }
    saveNote(t.value);
  }, true);

  /* ---------------------------------------------------------------------------
     DISCOUNT CODE

     Shopify's Ajax Cart API has no method to apply a discount. The only storefront route is a
     request to /discount/{CODE}, which sets the discount cookie server-side.

     The flow is: set the cookie -> re-read /cart.js -> only report success if the server
     actually reports a discount. The UI is never told "applied" by this script on its own, so
     a wrong code cannot produce a fake chip.

     THERE IS NO REMOVE HANDLER, ON PURPOSE — nothing can take a discount off a cart. It binds
     to the CART server-side, not the cookie. Full measurements in snippets/cart-promo.liquid.
     Never implement one by clearing the cart cookie: that silently empties the customer's cart.
  --------------------------------------------------------------------------- */

  /* Refresh the drawer THROUGH THE THEME, never by hand.

     DO NOT replace this with an innerHTML swap. A hand swap renders perfectly and leaves the
     cart DEAD: theme.js binds the quantity stepper and the remove control directly to the
     nodes after each of its own renders — it does not delegate. Verified 2026-09-03: after a
     manual swap, + and x looked normal and silently stopped changing the cart.

     Shopify.theme.ajaxCart.updateView(config, cart) does the fetch, the swap AND the
     rebinding. The neighbouring methods are traps: getAjaxCart(response) is only the parser,
     and initializeAjaxCart() just sets innerHTML — same dead-binding problem. */
  function rerender() {
    return readCart().then(function (cart) {
      try {
        var cfgEl = document.getElementById('cart-config');
        var api = window.Shopify && window.Shopify.theme && window.Shopify.theme.ajaxCart;
        if (cfgEl && cart && api && typeof api.updateView === 'function') {
          api.updateView(JSON.parse(cfgEl.textContent), cart);
          setTimeout(syncSlow, 500);
          return true;
        }
      } catch (e) { /* fall through to the reload below */ }
      window.location.reload();
      return false;
    });
  }

  function apply(code) {
    var el = actionsEl();
    if (!el || !code) return;
    var btn = el.querySelector('[data-og-promo-apply]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    setAttr(el, 'error', 'false');

    fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
      .then(function () { return readCart(); })
      .then(function (cart) {
        var ok = !!(cart && (
          (cart.discount_applications && cart.discount_applications.length) ||
          (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) ||
          cart.total_discounts > 0
        ));
        if (!ok) {
          var cur = actionsEl();
          if (cur) { setAttr(cur, 'error', 'true'); setAttr(cur, 'panel', 'promo'); }
          var b = cur && cur.querySelector('[data-og-promo-apply]');
          if (b) b.removeAttribute('aria-busy');
          return;
        }
        return rerender();
      })
      .catch(function () {
        var cur = actionsEl();
        if (cur) { setAttr(cur, 'error', 'true'); setAttr(cur, 'panel', 'promo'); }
      });
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest('[data-og-promo-apply]')) return;
    e.preventDefault();
    var el = actionsEl();
    var input = el && el.querySelector('[data-og-promo-input]');
    var code = input ? input.value.trim().toUpperCase() : '';
    if (code) apply(code);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var t = e.target;
    if (!t || !t.closest || !t.closest('[data-og-promo-input]')) return;
    e.preventDefault();
    var code = t.value.trim().toUpperCase();
    if (code) apply(code);
  });
})();